import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { take } from 'rxjs/operators';
import { DashboardService } from './dashboard.service';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  // Resolves to a messaging instance already bound to this app's own worker.
  // Left unbound, the SDK registers /firebase-messaging-sw.js at the domain
  // root, where WordPress answers the request with a 503.
  private messaging: Promise<any> | null = null;

  constructor(private afMessaging: AngularFireMessaging, private dashboardService: DashboardService,) {}

  // The registration that must own the push subscription. ngsw-worker.js controls
  // /client_app/, so navigator.serviceWorker.ready hands back Angular's worker —
  // binding the token to a worker with no push handler, which silently drops every
  // notification. Pick the Firebase worker by script name instead.
  private static readonly SW_URL = '/client_app/firebase-messaging-sw.js';
  private static readonly SW_SCOPE = '/client_app/';

  // Registers this app's worker directly instead of waiting on index.html.
  //
  // The previous version started from navigator.serviceWorker.ready, which never
  // settles while no worker controls the page — it does not reject, it hangs. So
  // whenever the registration was missing or its install failed, getToken() and
  // listenForMessages() stalled forever with no token, no error and no log line.
  // Registering here is idempotent (the browser returns the existing registration
  // when the script and scope match) and turns that silent hang into a real error.
  //
  // It also removes the getRegistrations()[0] guess: one origin serves both
  // /client_app/ and /driver_app/, each with its own firebase-messaging-sw.js, so
  // picking the first match bound this app's token to whichever app the browser
  // registered first. Naming the script rules that out.
  private getPushRegistration(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      return Promise.reject(new Error('serviceWorker API unavailable'));
    }
    return navigator.serviceWorker
      .register(NotificationsService.SW_URL, { scope: NotificationsService.SW_SCOPE })
      .then(reg => this.whenActive(reg))
      .catch(err => {
        console.error('SW registration FAILED:', err);
        throw err;
      });
  }

  // pushManager.subscribe() requires an *active* worker — during an install or a
  // script swap the scope briefly has none, which surfaced as
  // "AbortError: Subscription failed - no active Service Worker".
  private whenActive(reg: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
    if (reg.active) { return Promise.resolve(reg); }
    const worker = reg.installing || reg.waiting;
    if (!worker) { return Promise.resolve(reg); }
    return new Promise(resolve => {
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') { resolve(reg); }
      });
    });
  }

  private getMessaging(): Promise<any> {
    if (!this.messaging) {
      // serviceWorker.ready resolves only once a worker is active AND controlling
      // the page, which is the state getToken() requires (notably on iOS).
      this.messaging = this.getPushRegistration()
        .then(swReg =>
          this.afMessaging.messaging.pipe(take(1)).toPromise().then(messaging => {
            try {
              (messaging as any).useServiceWorker(swReg);
              (messaging as any).usePublicVapidKey(environment.firebase.vapidKey);
            } catch (e) {
              // Both throw if called twice on one instance — safe to ignore.
            }
            return messaging;
          }),
        )
        .catch(err => {
          this.messaging = null; // don't cache the failure — allow a later retry
          throw err;
        });
    }
    return this.messaging;
  }

  private fetchToken(): Promise<void> {
    return this.getMessaging()
      .then(messaging => messaging.getToken())
      .then(token => {
        console.log('Push Notification Token:', token);
        if (!token) {
          console.warn('getToken() returned null — the worker may not control the page yet');
          return;
        }
        this.dashboardService.updateToken(token).subscribe(
          result => {
            console.log(result);
          },
          error => console.log(error),
        );
      })
      .catch(err => {
        console.error('Push Notification Error:', err);
      });
  }

  requestPermission(): Promise<void> {
    // iOS below 16.4, and Safari outside standalone mode, expose no Notification API.
    if (typeof Notification === 'undefined') {
      console.warn('Notification API unavailable — the app must be added to the home screen first.');
      return Promise.resolve();
    }

    // Settle permission before touching Firebase. Firebase 7's getToken() requests
    // it internally and misreads the async result on iOS, reporting a spurious
    // permission-blocked error.
    return Notification.requestPermission()
      .then(permission => {
        if (permission !== 'granted') {
          console.warn('Notification permission not granted:', permission);
          return;
        }
        return this.fetchToken();
      })
      .catch(err => {
        console.error('Push Notification Error:', err);
      });
  }

  refreshToken(): Promise<void> {
    // Routed through getMessaging() on purpose. Reading the token straight off
    // AngularFireMessaging asks the SDK for its default root-scoped worker,
    // which is the 503 this whole class exists to avoid.
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return Promise.resolve();
    }
    return this.fetchToken();
  }

  listenForMessages(): Promise<void> {
    // Routed through getMessaging(), not afMessaging.messages: the latter
    // subscribes to the SDK's default root-scoped instance, which is not the
    // worker holding our push subscription, so foreground messages never arrived.
    return this.getMessaging()
      .then(messaging => {
        messaging.onMessage((payload: any) => {
          console.log('New Notification:', payload);
          this.showForegroundNotification(payload);
        });
      })
      .catch(err => {
        console.error('listenForMessages() failed:', err);
      });
  }

  // Firebase shows nothing itself once it finds a visible window: it hands the
  // message to onMessage and skips the worker's notification path entirely. So
  // with the app on screen, nothing announces the update unless we raise it here.
  private showForegroundNotification(payload: any): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') { return; }

    const n = payload.notification || {};
    const d = payload.data || {};

    // Raised through the registration rather than `new Notification()`: the
    // constructor is unsupported on Android Chrome, and only a worker-shown
    // notification reaches the notificationclick handler that opens the ride.
    this.getPushRegistration()
      .then(reg => reg.showNotification(n.title || d.title || 'AppaTaxi', {
        body: n.body || d.body || '',
        icon: '/client_app/assets/icon/car-logo-notification.png',
        // Re-delivery of the same ride replaces its banner instead of stacking.
        tag: d.ride_id ? 'ride-' + d.ride_id : 'appataxi',
        data: { click_action: d.click_action },
      }))
      .catch(err => console.error('foreground showNotification failed:', err));
  }

}
